import { CopyOutlined } from "@ant-design/icons";
import { Button, Drawer, Form, Input, message, Popover, Row } from "antd";
import * as AUTH_SERVICES from "api/services/auth/auth.services";
import { StatusCode, UserRoles } from "common/enums";
import AppButton from "components/libs/button";
import { getAuth, sendPasswordResetEmail } from "firebase/auth";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { useLocation } from "react-router-dom";
import { WhatsappIcon, WhatsappShareButton } from "react-share";
import s from "./password.module.css";

type Props = {
    visible: boolean,
    onClose: () => void,
    userData?: any,
    userType?: string,
}

const SendUpdatePasswordRequestDrawer: React.FC<Props> = ({ visible, onClose, userData, userType }) => {

    const [isMobile, setIsMobile] = useState(window.innerWidth <= 767);
    const handleResize = () => {
        setIsMobile(window.innerWidth <= 767);
    };
    window.addEventListener('resize', handleResize);

    const location = useLocation();

    //!HOOKS
    const { t } = useTranslation();
    const auth = useSelector((state) => state.auth);
    const [formInfos] = Form.useForm();
    const { user } = useSelector((state) => state.auth);

    //!STATES
    const [isLoading, setIsLoading] = useState<boolean>(false);
    const [displayForm, setDisplayForm] = useState<boolean>(false);
    const isParent = auth.user?.roles?.includes(UserRoles.PARENT);
    const isOrganism = auth.user?.roles?.includes(UserRoles.ORGANISME);

    //!CONSTANTS
    const validateMessages = {
        required: t("auth.error.required"),
        types: {
            email: t("auth.error.emailInvalid"),
        },
    };

    const closeDrawer = async () => {
        formInfos.resetFields();
        setSuccessMessage("");
        onClose();
    }

    const [currentEmail, setCurrentEmail] = useState('');
    const [newPassword, setNewPassword] = useState('');
    const [confirmNewPassword, setConfirmNewPassword] = useState('');
    const [codeOTP, setCodeOTP] = useState('');
    const [error, setError] = useState<any>(null);
    const [successMessage, setSuccessMessage] = useState<string>("");

    const handlePasswordRequest = async () => {

        const userEmail = user?.email;

        if (isParent) {
            if (userEmail === currentEmail) {
                sendPasswordRequest();
            }
            else {
                setError("Cette addresse email est différente de la tienne. Entre ton adresse email!");
            }
        }
        else if (isOrganism) {
            if (userEmail === currentEmail) {
                sendPasswordRequest();
            }
            else {
                setError("Cette addresse email est différente de la tienne. Entre ton adresse email!");
            }
        }
        else {
            ///sendPasswordRequest();
            sendOTPToUser();
        }

    };

    const sendPasswordRequest = async () => {
        setIsLoading(true);
        try {
            const auth = getAuth();
            await sendPasswordResetEmail(auth, currentEmail);
            setSuccessMessage((userType === "parent" || userType === "organism") ? 'Email de réinitialisation du mot de passe envoyé avec succès. Merci de consulter vos emails et vérifer aussi vos spams.' : 'Email de réinitialisation du mot de passe envoyé avec succès. Merci de consulter tes emails et vérifer aussi tes spams');
            setIsLoading(false);
        } catch (error) {
            setError("Adresse mail introuvable. Merci d'essayer plus tard.");
            setIsLoading(false);
        }
    }

    const sendOTPToUser = async () => {

        const role = location.pathname.split("/").pop();
        setIsLoading(true);

        try {
            await AUTH_SERVICES.requestOTP(currentEmail, role!);
            setDisplayForm(true);
        } catch (error: any) {
            console.log(error);
        } finally {
            setIsLoading(false);
        }
    }

    const handleUpdatePassword = async () => {
        if (!currentEmail && !newPassword && !confirmNewPassword && !codeOTP) {
            setError("Remplis le formulaire pour réinitialiser ton mot de passe !");
            return;
        }

        if (newPassword !== confirmNewPassword) {
            setError("les mots de passe sont différents !");
            return;
        }

        try {
            setIsLoading(true);
            const response = await AUTH_SERVICES.resetPassword(currentEmail, codeOTP, newPassword, confirmNewPassword);
            if (response?.statusCode === StatusCode.CREATED) {
                message.success("Réinitialisation du mot de passe réussie !");
                onClose();
            }
            else {
                message.warning("Une erreur est survenue, réessayer plus tard !");
            }
        } catch (error: any) {
            let content = "";
            for (
                let index = 0;
                index < error?.response?.data?.message?.length;
                index++
            ) {
                content = error?.response?.data?.message[index] + ", " + content;
            }
            message.error({
                content: content,
            });
        } finally {
            setIsLoading(false);
        }
    }

    const [visibleSocialMedia, setVisibleSocialMedia] = useState(false);
    const hide = () => setVisibleSocialMedia(false);
    const handleVisibleChange = () => setVisibleSocialMedia(!visibleSocialMedia);

    const handleCopy = () => {
        const textToCopy = `"Email: ${userData?.email}, Mot de passe: ${(userData?.password !== "undefined" && userData?.password !== "") ? userData?.password : "123456"}"`;
        navigator.clipboard.writeText(textToCopy).then(() => {
            message.success('Accès copiés');
        }).catch(() => {
            message.error('Erreur lors de la copie!');
        });
    };

    return (
        <div>
            <Drawer
                title={<div style={{ display: "flex", flexDirection: "row", alignItems: "center", justifyContent: "flex-end" }}>
                    <div style={{ textTransform: "uppercase", fontSize: "1.2em", margin: "0px auto" }}>{(isParent || isOrganism) ? "Accès élève" : "Mot de passe oublié"}</div>
                </div>}
                placement="right"
                closable={true}
                onClose={closeDrawer}
                visible={visible}
                width={isMobile ? "100%" : "70%"}
                headerStyle={{ display: "flex", justifyContent: "space-between" }}
                bodyStyle={{ background: "#FFF", padding: "0px" }}
            >
                <div style={{ alignItems: "flex-start", margin: "40px auto" }}>

                    {!userData && <div style={{ margin: "0px auto", textAlign: "center" }}>{(userType === "parent" || userType === "organism") ? "Entrez votre adresse email et après vérification nous vous enverrons un email pour réinitialiser votre mot de passe." : "Entre ton adresse email et après vérification nous t'enverrons un email pour réinitialiser ton mot de passe."}</div>}

                    <Form
                        validateTrigger={["onFinish"]}
                        validateMessages={validateMessages}
                        name="registerParent"
                        autoComplete="off"
                        form={formInfos}
                        onFinish={displayForm ? handleUpdatePassword : handlePasswordRequest}
                    >
                        <div style={{ width: "55%", margin: "20px auto" }}>

                            {(isParent || isOrganism) && <div style={{ marginLeft: "10%", marginRight: "10%", display: "flex", flexDirection: "column", alignItems: "flex-start", justifyContent: "flex-start" }}>
                                <div style={{ fontSize: "1.2em", fontWeight: "400", marginTop: "0px" }}>Username : {userData?.email}</div>
                                <div style={{ fontSize: "1.2em", fontWeight: "400", marginTop: "20px" }}>Mot de passe : {(userData?.password !== "undefined" && userData?.password !== "") ? userData?.password : "123456"}</div>
                            </div>}

                            {(isParent || isOrganism) && <div style={{ margin: "30px auto", width: "220px" }}>
                                <Popover
                                    content={
                                        <>
                                            <div style={{ display: "flex", flexDirection: "row" }}>
                                                <div>
                                                    {" "}
                                                    <WhatsappShareButton title={"Accès élève"} url={`"Email: ${userData?.email}, Mot de passe: ${(userData?.password !== "undefined" && userData?.password !== "") ? userData?.password : "123456"}"`}>
                                                        <WhatsappIcon size={32} round={true} />
                                                    </WhatsappShareButton>
                                                </div>

                                                <div>
                                                    {" "}
                                                    <Button
                                                        type="primary"
                                                        icon={<CopyOutlined />}
                                                        onClick={handleCopy}
                                                        style={{ borderRadius: "50%", marginLeft: "10px" }}
                                                    >
                                                    </Button>
                                                </div>
                                            </div>
                                            <div style={{ width: "100%", textAlign: "center" }}>
                                                <a
                                                    onClick={(e) => {
                                                        e.preventDefault();
                                                        hide();
                                                    }}
                                                    href="/"
                                                >
                                                    Fermer
                                                </a>
                                            </div>
                                        </>
                                    }
                                    trigger="click"
                                    visible={visibleSocialMedia}
                                    onVisibleChange={handleVisibleChange}
                                >
                                    {" "}
                                    <AppButton className={s.shareButton}>partager</AppButton>

                                </Popover>
                            </div>
                            }

                            {(!isParent && !isOrganism) && <Form.Item
                                name="email"
                                rules={[{ required: true }]}
                                style={{ marginBottom: "20px" }}
                            >
                                <Input
                                    type="email"
                                    className={s.field}
                                    placeholder={"Entre ton adresse email"}
                                    value={currentEmail}
                                    onChange={(e) => setCurrentEmail(e.target.value)}
                                    style={{ width: "100%" }}
                                />
                            </Form.Item>}

                            {(!isParent && !isOrganism) && displayForm && <Form.Item
                                name="password"
                                rules={[{ required: true }]}
                                style={{ marginBottom: "20px" }}
                            >
                                <Input
                                    type="password"
                                    className={s.field}
                                    placeholder={"Entre ton mot de passe"}
                                    value={currentEmail}
                                    onChange={(e) => setNewPassword(e.target.value)}
                                    style={{ width: "100%" }}
                                />
                            </Form.Item>}

                            {(!isParent && !isOrganism) && displayForm && <Form.Item
                                name="confirmPassword"
                                rules={[{ required: true }]}
                                style={{ marginBottom: "20px" }}
                            >
                                <Input
                                    type="password"
                                    className={s.field}
                                    placeholder={"Confirme ton mot de passe"}
                                    value={currentEmail}
                                    onChange={(e) => setConfirmNewPassword(e.target.value)}
                                    style={{ width: "100%" }}
                                />
                            </Form.Item>}

                            {(!isParent && !isOrganism) && displayForm && <Form.Item
                                name="OTP"
                                rules={[{ required: true }]}
                                style={{ marginBottom: "20px" }}
                            >
                                <Input
                                    type="text"
                                    className={s.field}
                                    placeholder={"Entre le code OTP reçu par email"}
                                    value={currentEmail}
                                    onChange={(e) => setCodeOTP(e.target.value)}
                                    style={{ width: "100%" }}
                                />
                            </Form.Item>}

                        </div>

                        {(!isParent && !isOrganism) && <Row justify="center" style={{ marginTop: "30px" }}>
                            <Form.Item>
                                <AppButton loading={isLoading} htmlTypeSubmit={true}><a className={s.nextButton}>Valider</a></AppButton>
                            </Form.Item>
                        </Row>}
                    </Form>
                    {error && <div style={{ color: 'red', margin: "0px auto", textAlign: "center" }}>{error}</div>}
                    {successMessage && successMessage !== "" && <div style={{ color: 'green', margin: "0px auto", textAlign: "center" }}>{successMessage}</div>}
                </div>

            </Drawer>
        </div>
    );

};

export default SendUpdatePasswordRequestDrawer;